// React imports
import { useState } from 'react';
import { StateError } from '../errorHandling';



// ----------------------------------------------------------------------------
//   State Enum
// ----------------------------------------------------------------------------


/**
 * Enum representing the current state of data to be loaded from the API.
 * May be used to tracks updates / actions within the UI.
 */
export const LoadedDataState = {

  // Default value -- data has not been requested
  NONE:    "NONE",

  // Data has been requested and is loading
  WORKING: "WORKING",

  // Data was loaded successfully
  SUCCESS: "SUCCESS",

  // Data was not loaded successfully
  FAILURE: "FAILURE",
}



// ----------------------------------------------------------------------------
//   Wrapped Value Interface
// ----------------------------------------------------------------------------


/**
 * Struct wrapping a pending value with its load state.
 * Bundles value, load state, and error together for React states.
 */
// export interface LoadedData<Type> {
//   state:  LoadedDataState
//   value?: Type
//   error?: any
// }



// ----------------------------------------------------------------------------
//   Constructors / Wrappers
// ----------------------------------------------------------------------------


/**
 * Constant struct representing the NONE (empty) state
 */
export const LoadedDataNone = { state: LoadedDataState.NONE }


/**
 * Constant struct representing the WORKING state
 */
export const LoadedDataWorking = { state: LoadedDataState.WORKING }


/**
 * Wrap a loaded value in a SUCCESS state object
 */
export function LoadedDataSuccess(value) {
  return { state: LoadedDataState.SUCCESS, value }
}


/**
 * Wrap an error value in a FAILURE state object
 */
export function LoadedDataFailure(error) {
  return { state: LoadedDataState.FAILURE, error }
}


/**
 * Combine state wrapper functions into one object that can be imported more succinctly
 */
export const LoadedData = {
  NONE:    LoadedDataNone,
  WORKING: LoadedDataWorking,
  SUCCESS: LoadedDataSuccess,
  FAILURE: LoadedDataFailure,
}



// ----------------------------------------------------------------------------
//   Custom React Hook(s)
// ----------------------------------------------------------------------------


// export interface LoadedDataManager<Type> {
//   clearData:   ()            => void;
//   setWorking:  ()            => void;
//   setSuccess:  (value: Type) => void;
//   setFailure:  (error: any)  => void;
//   updateValue: (value: Type) => void;
// }

/**
 * Custom React Hook to manage a LoadedData object.
 */
export function useLoadedData() {

  // Call React useState hook
  const [ data, setData ] = useState(LoadedData.NONE);

  // Return state object and manager object
  return [ data, {

    // Reset to NONE state
    clearData: () => {
      setData({ state: LoadedDataState.NONE });
    },

    // Mark the data as loading
    setWorking: () => {
      setData({ state: LoadedDataState.WORKING });
    },

    // Update the state to store the loaded value
    setSuccess: (value) => {
      setData({ state: LoadedDataState.SUCCESS, value });
    },

    // Update the state to track the error, including Sentry logging
    setFailure: (state, error, track=true) => {
      const errorObject = new StateError( state, error, { track } );
      setData({ state: LoadedDataState.FAILURE, error: errorObject });
    },

    // Set the stored value directly to the given object
    // Useful for updating objects after they've been loaded
    updateValue: (value) => {
      setData({ state: LoadedDataState.SUCCESS, value });
    },
  }];
}
